import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SettingsIcon from '@mui/icons-material/Settings';
import {
	QueryClient,
	QueryClientProvider,
	useMutation,
	useQuery,
} from '@tanstack/react-query';
import { createBrowserHistory } from 'history';
import jwt_decode from 'jwt-decode';
import {
	SovosCircularProgress,
	SovosErrorPage,
	SovosGlobalLayout,
	SovosProvider,
} from 'mosaic-react';
import React, { useCallback, useEffect, useState } from 'react';
import {
	unstable_HistoryRouter as HistoryRouter,
	Route,
	Routes,
	useLocation,
	useNavigate,
	useSearchParams,
} from 'react-router-dom';

import {
	fetchOrgs,
	fetchUser,
	getOrgInfo,
	logoutUser,
	otpLogin,
	renewToken,
} from './api/user';
import './axiosInterceptors';
import {
	JWT_SESSION_STORAGE,
	ORG_INFO_SESSION_STORAGE,
	ORG_SESSION_STORAGE,
	OTP_SESSION_STORAGE,
	USER_ID_SESSION_STORAGE,
	USER_INFO_SESSION_STORAGE,
} from './helpers/constant';
import { initializePendo } from './helpers/pendoInitializer';
import type { OTPLoginData, UserJWT } from './interfaces/User';
import LocationsMappingsPage from './pages/locationMapping/LocationMappingsPage';
import LocationsMappingEditor from './pages/locationMapping/locationMappingEditor/locationMappingEditor';
import NewRateFilePage from './pages/newRateFile/NewRateFilePage';
import RateFilesPage from './pages/rateFiles/RateFilesPage';
import SubscriptionSettingsPage from './pages/subscriptionSettings/SubscriptionSettingsPage';

const App: React.FC<{ userId: string }> = ({ userId }) => {
	const location = useLocation();
	const navigate = useNavigate();

	const userQuery = useQuery(['user'], () => fetchUser(userId));

	const logoutMutation = useMutation(logoutUser);

	const navLinks = [
		{
			id: '/',
			label: 'Rate Files',
			icon: InsertDriveFileIcon,
			action: () => navigate('/'),
			isDefault: true,
		},
		{
			id: '/location-mappings',
			label: 'Location Mappings',
			icon: LocationOnIcon,
			action: () => navigate('/location-mappings'),
		},
		{
			id: '/settings',
			label: 'Settings',
			icon: SettingsIcon,
			nestedLinks: [
				{
					id: '/subscription-settings',
					label: 'Subscription Settings',
					action: () => navigate('/subscription-settings'),
				},
			],
		},
	];

	const navLinksIds = navLinks.flatMap((link) => {
		return link.id;
	});

	const topLinkPath = `/${location.pathname.split('/')[1]}`;

	const navProps = {
		product: 'Sovos Sales Tax for POS',

		links: navLinks,
		initialSelectedLink: '',
		selectedLink: navLinksIds.includes(topLinkPath) ? topLinkPath : '/',

		FooterProps: {
			userName: userQuery.data
				? userQuery.data.firstName + ' ' + userQuery.data.lastName
				: '',
			settingsMenuItems: [],
			userMenuItems: [
				{
					label: 'Logout',
					action: () => {
						logoutMutation.mutate();
					},
				},
			],
			showHelpButton: true,
			onHelpClicked: () => {},
		},

		// Throws error if not included. Don't know what it does, not documented.
		setAccount: () => {},
	};

	return (
		<SovosGlobalLayout NavigationProps={navProps} width='100%'>
			<Routes>
				<Route path='/' element={<RateFilesPage />} />
				<Route path='/new' element={<NewRateFilePage />} />
				<Route path='/location-mappings' element={<LocationsMappingsPage />} />
				<Route
					path='/location-mappings/new'
					element={<LocationsMappingEditor />}
				/>
				<Route
					path='/location-mappings/:locationMappingID'
					element={<LocationsMappingEditor />}
				/>
				<Route
					path='/subscription-settings'
					element={<SubscriptionSettingsPage />}
				/>
				<Route
					path='*'
					element={
						<SovosErrorPage
							errorCode='404'
							actionUrl='/'
							helpUrl='https://sovos.com/support/'
						/>
					}
				/>
			</Routes>
		</SovosGlobalLayout>
	);
};

const AuthWrapper: React.FC = () => {
	const navigate = useNavigate();

	const [searchParams] = useSearchParams();

	const otp = searchParams.get('otp');
	const org = searchParams.get('org');

	const [loading, setLoading] = useState(true);

	const [userId, setUserId] = useState<
		OTPLoginData['userProfile']['userId'] | null
	>(sessionStorage.getItem(USER_ID_SESSION_STORAGE));

	const [tokenExpiration, setTokenExpiration] = useState<number | null>(null);

	const handleJwtChange = useCallback(() => {
		const userIdChange = sessionStorage.getItem(USER_ID_SESSION_STORAGE);

		if (userId !== userIdChange) {
			setUserId(userIdChange);
		}

		const jwt = sessionStorage.getItem(JWT_SESSION_STORAGE);

		if (jwt) {
			const { exp } = jwt_decode<UserJWT>(jwt);

			if (tokenExpiration !== exp) {
				setTokenExpiration(exp);
			}
		}
	}, [userId, tokenExpiration]);

	// handles state update on jwt session storage change
	useEffect(() => {
		handleJwtChange();

		window.addEventListener('jwt', handleJwtChange);

		return () => window.removeEventListener('jwt', handleJwtChange);
	}, [handleJwtChange]);

	// handles loading state and otp login
	useEffect(() => {
		async function login() {
			if (otp) {
				sessionStorage.clear();
				sessionStorage.setItem(OTP_SESSION_STORAGE, otp);

				if (org) {
					sessionStorage.setItem(ORG_SESSION_STORAGE, org);
				}

				const userIds = await otpLogin(otp);
				if (userIds.platformToken) {
					sessionStorage.setItem(JWT_SESSION_STORAGE, userIds.platformToken);
					sessionStorage.setItem(USER_ID_SESSION_STORAGE, userIds.userId);
					const userInfo = await fetchUser(userIds.userId);
					if (userInfo) {
						sessionStorage.setItem(
							USER_INFO_SESSION_STORAGE,
							JSON.stringify(userInfo)
						);
					}
					window.dispatchEvent(new Event('jwt'));
				}

				navigate('/', { replace: true });
			}
			const userOrgs = await fetchOrgs();
			if (userOrgs && org) {
				sessionStorage.setItem(
					ORG_INFO_SESSION_STORAGE,
					JSON.stringify(getOrgInfo(userOrgs))
				);
			}
			if (loading && !otp) {
				setLoading(false);
			}
			initializePendo();
		}

		login();
	}, [navigate, otp, org, loading, handleJwtChange]);

	// handles token renewal
	useEffect(() => {
		if (tokenExpiration) {
			const timeNowSeconds = Date.now() / 1000;

			const fiveMinSeconds = 300;

			const renewSeconds = tokenExpiration - timeNowSeconds - fiveMinSeconds;

			const renewTimeout = setTimeout(() => {
				renewToken();
			}, renewSeconds * 1000);

			return () => clearTimeout(renewTimeout);
		}
	}, [tokenExpiration]);

	if (loading) {
		return (
			<div
				style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<SovosCircularProgress />
			</div>
		);
	}

	if (userId) {
		return <App userId={userId} />;
	}

	// redirect to login page if no otp and not logged in
	window.location.href =
		document.referrer ||
		(window.location.hostname.startsWith('integration')
			? 'https://integration-s1.sovos.org'
			: 'https://s1.sovos.org');

	return null;
};

const queryClient = new QueryClient();

const DevOTPWrapper = React.lazy(() => import('./dev/DevOTPWrapper'));

const AppWrapper: React.FC = () => (
	<HistoryRouter history={createBrowserHistory({ window })}>
		<SovosProvider>
			<QueryClientProvider client={queryClient}>
				{process.env.NODE_ENV === 'development' ? (
					<DevOTPWrapper>
						<AuthWrapper />
					</DevOTPWrapper>
				) : (
					<AuthWrapper />
				)}
			</QueryClientProvider>
		</SovosProvider>
	</HistoryRouter>
);

export default AppWrapper;
